<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

// Selector del panel de la conversación: etiqueta a la izquierda y un chip
// a la derecha, con el mismo aspecto que Tipo y Estado del contacto. Las
// opciones llegan como { id, name, thumbnail }.
const props = defineProps({
  label: { type: String, required: true },
  options: { type: Array, default: () => [] },
  selected: { type: Object, default: null },
  placeholder: { type: String, default: '' },
  showThumbnail: { type: Boolean, default: false },
  showSearch: { type: Boolean, default: true },
});

const emit = defineEmits(['select']);

const [isOpen, toggleOpen] = useToggle();

const menuItems = computed(() =>
  props.options.map(option => ({
    label: option.name,
    value: option.id ?? 'none',
    action: 'select',
    thumbnail:
      props.showThumbnail && option.id
        ? { name: option.name, src: option.thumbnail }
        : undefined,
    isSelected: (props.selected?.id ?? null) === (option.id ?? null),
  }))
);

const onAction = ({ value }) => {
  toggleOpen(false);
  const id = value === 'none' ? null : value;
  if ((props.selected?.id ?? null) === id) return;
  // Se emite siempre una opción: quien escucha espera un objeto.
  const option = props.options.find(item => (item.id ?? null) === id);
  if (option) emit('select', option);
};
</script>

<template>
  <div
    v-on-clickaway="() => toggleOpen(false)"
    class="relative flex items-center gap-2.5 min-h-[26px] min-w-0"
  >
    <span class="flex-shrink-0 w-[78px] text-[11.5px] text-n-slate-10">
      {{ label }}
    </span>
    <button
      type="button"
      class="inline-flex items-center gap-1.5 h-[26px] ltr:pl-1.5 ltr:pr-2 rtl:pr-1.5 rtl:pl-2 rounded-md min-w-0 max-w-full text-xs font-semibold truncate transition-colors"
      :class="
        selected
          ? 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3'
          : 'border border-dashed border-n-slate-7 text-n-slate-10 hover:border-n-brand hover:text-n-blue-text'
      "
      @click="toggleOpen()"
    >
      <Avatar
        v-if="showThumbnail && selected"
        :name="selected.name"
        :src="selected.thumbnail"
        :size="18"
      />
      <span class="truncate">{{ selected?.name || placeholder }}</span>
      <Icon
        icon="i-lucide-chevron-down"
        size="size-3 flex-shrink-0 opacity-55"
      />
    </button>
    <DropdownMenu
      v-if="isOpen"
      :menu-items="menuItems"
      :show-search="showSearch"
      class="z-30 mt-1.5 top-full left-0 right-0 max-h-64 overflow-y-auto"
      @action="onAction"
    />
  </div>
</template>
